import React, { useEffect, useMemo, useState } from 'react';
import { HoverInsight } from '../types';
import MetricCard from '../components/MetricCard';
import api from '../lib/api';
import { DateRangePicker } from '../components/DateRangePicker';
import { inr, num, pctFmt, toISO, deltaMeta, snapshotMeta, Tile, SnapshotTile } from '../lib/metricFormat';
import { SortableTileGrid } from '../components/SortableTileGrid';
import { useCategoryTileOrder, TileCategory } from '../hooks/useCategoryTileOrder';
import { RotateCcw } from 'lucide-react';

interface ScorecardProps {
  onHover: (insight: HoverInsight | null) => void;
}

interface ScorecardData {
  period: { start_date: string; end_date: string };
  previous_period: { start_date: string; end_date: string };
  mtd: { start_date: string; end_date: string };
  connections: { zoho: boolean; shopify: boolean; ads: boolean; unicommerce: boolean; sheets: boolean; design_sheet: boolean };
  tiles: {
    dtc_revenue: Tile; dtc_revenue_mtd: Tile; b2b_revenue: Tile; b2b_revenue_mtd: Tile;
    po_status_india: SnapshotTile; po_status_china: SnapshotTile;
    total_ad_spend: Tile; roas: Tile; avg_ctr: Tile;
    repeat_rate: Tile;
    cart_recovery_rate: Tile; cart_recovery_revenue: Tile; assisted_revenue: Tile;
    designs_shipped: SnapshotTile; sampling_finished: SnapshotTile;
    stock_in_rate: SnapshotTile; dtc_orders_shipped: Tile;
  };
}

type Preset = 'weekly' | 'monthly' | 'quarterly';

// Category + row order exactly as specified in the client's "Scorecard Module Final" sheet.
// Drag-and-drop reordering stays confined within each category's own grid.
const CATEGORIES: TileCategory[] = [
  { key: 'revenue', label: 'Revenue', tiles: ['dtc_revenue', 'dtc_revenue_mtd', 'b2b_revenue', 'b2b_revenue_mtd'] },
  { key: 'production', label: 'Production', tiles: ['po_status_india', 'po_status_china'] },
  { key: 'performance_ads', label: 'Performance Ads', tiles: ['total_ad_spend', 'roas', 'avg_ctr'] },
  { key: 'retention', label: 'Retention', tiles: ['repeat_rate'] },
  { key: 'crm', label: 'CRM', tiles: ['cart_recovery_rate', 'cart_recovery_revenue', 'assisted_revenue'] },
  { key: 'product', label: 'Product', tiles: ['designs_shipped', 'sampling_finished'] },
  { key: 'merchandising', label: 'Merchandising', tiles: ['stock_in_rate'] },
  { key: 'warehouse', label: 'Warehouse', tiles: ['dtc_orders_shipped'] },
];

// Rolling windows ending YESTERDAY (not today) — today's data is still incomplete/accumulating
// (e.g. only a few hours of orders, no invoices issued yet at the top of a new month), which is
// exactly what made MTD/revenue tiles flash near-zero right after a day/month rollover. Ending
// at yesterday keeps every preset anchored to a fully-closed day, so MTD always reflects at
// least one full day of real data instead of a sliver of the current, still-ticking day.
// e.g. if today is 07 Aug: Weekly = 31 Jul–06 Aug, Monthly = 07 Jul–06 Aug, Quarterly = 07 May–06 Aug.
function computePreset(preset: Preset, today: Date): { from: Date; to: Date } {
  const to = new Date(today);
  to.setDate(today.getDate() - 1);
  let from: Date;
  if (preset === 'weekly') {
    from = new Date(to);
    from.setDate(to.getDate() - 6);
  } else if (preset === 'monthly') {
    from = new Date(to.getFullYear(), to.getMonth() - 1, to.getDate());
  } else {
    from = new Date(to.getFullYear(), to.getMonth() - 3, to.getDate());
  }
  // keep every preset within the backend's 90-day cap (Quarterly = ~90-92 days depending on
  // month lengths, so this clamp is regularly in effect there, not just an edge case)
  const maxSpanMs = 89 * 86400000;
  if (to.getTime() - from.getTime() > maxSpanMs) from = new Date(to.getTime() - maxSpanMs);
  return { from, to };
}

const cardBorder = '!border !border-black/70';

export default function Scorecard({ onHover }: ScorecardProps) {
  const today = useMemo(() => new Date(), []);
  const ninetyDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 89); return d; }, []);
  const [preset, setPreset] = useState<Preset>('monthly');
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => computePreset('monthly', today));
  const [data, setData] = useState<ScorecardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [order, reorderCategory, resetOrder] = useCategoryTileOrder('scorecard-tile-order-v2', CATEGORIES);

  useEffect(() => {
    setLoading(true);
    const params = { start_date: toISO(range.from), end_date: toISO(range.to) };
    api.get('/scorecard', { params })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [range]);

  const handlePreset = (p: Preset) => {
    setPreset(p);
    setRange(computePreset(p, today));
  };

  const handleCalendarChange = (next: { from: Date; to: Date }) => {
    setRange(next);
    // if the custom range happens to exactly match a preset, keep that preset highlighted; else clear
    const match = (['weekly', 'monthly', 'quarterly'] as Preset[]).find((p) => {
      const c = computePreset(p, today);
      return toISO(c.from) === toISO(next.from) && toISO(c.to) === toISO(next.to);
    });
    setPreset(match || (null as any));
  };

  const t = data?.tiles;

  const renderTile = (id: string) => {
    switch (id) {
      case 'dtc_revenue':
        return <MetricCard className={cardBorder} platform="Shopify" title="DTC Revenue" value={t ? inr(t.dtc_revenue.value) : '—'} change={deltaMeta(t?.dtc_revenue).change} trend={deltaMeta(t?.dtc_revenue).trend} description="Shopify Net Sales for the selected period" onHover={onHover} />;
      case 'dtc_revenue_mtd':
        return <MetricCard className={cardBorder} platform="Calculated" title="DTC Revenue (MTD, ex-GST)" value={t ? inr(t.dtc_revenue_mtd.value) : '—'} change={deltaMeta(t?.dtc_revenue_mtd).change} trend={deltaMeta(t?.dtc_revenue_mtd).trend} description="Always Month-to-Date, Net Sales ÷ 1.18 (18% GST removed)" onHover={onHover} />;
      case 'b2b_revenue':
        return <MetricCard className={cardBorder} platform="Zoho Books" title="B2B Revenue" value={t ? inr(t.b2b_revenue.value) : '—'} change={deltaMeta(t?.b2b_revenue).change} trend={deltaMeta(t?.b2b_revenue).trend} description="Zoho invoices for the selected period, excl. Shopify-named customers" onHover={onHover} />;
      case 'b2b_revenue_mtd':
        return <MetricCard className={cardBorder} platform="Calculated" title="B2B Revenue (MTD, ex-GST)" value={t ? inr(t.b2b_revenue_mtd.value) : '—'} change={deltaMeta(t?.b2b_revenue_mtd).change} trend={deltaMeta(t?.b2b_revenue_mtd).trend} description="Always Month-to-Date, Zoho invoices ÷ 1.18 (18% GST removed)" onHover={onHover} />;
      case 'po_status_india':
        return <MetricCard className={cardBorder} platform="Category Ops Sheet" title="PO Status Count — India" value={t ? num(t.po_status_india.value) : '—'} change={snapshotMeta(t?.po_status_india).change} trend={snapshotMeta(t?.po_status_india).trend} description="Live count of India POs: In-Transit + In Production + Delayed" onHover={onHover} />;
      case 'po_status_china':
        return <MetricCard className={cardBorder} platform="Category Ops Sheet" title="PO Status Count — China" value={t ? num(t.po_status_china.value) : '—'} change={snapshotMeta(t?.po_status_china).change} trend={snapshotMeta(t?.po_status_china).trend} description="Live count of China POs: In-Transit + In Production + Delayed" onHover={onHover} />;
      case 'total_ad_spend':
        return <MetricCard className={cardBorder} platform="Meta + Google" title="Total Ad Spend" value={t ? inr(t.total_ad_spend.value) : '—'} change={deltaMeta(t?.total_ad_spend).change} trend={deltaMeta(t?.total_ad_spend).trend} description="Meta + Google Ads spend for the selected period" onHover={onHover} />;
      case 'roas':
        return <MetricCard className={cardBorder} platform="Calculated" title="ROAS" value={t ? `${t.roas.value.toFixed(2)}x` : '—'} change={deltaMeta(t?.roas).change} trend={deltaMeta(t?.roas).trend} description="Total Revenue ÷ Total Ad Spend" onHover={onHover} />;
      case 'avg_ctr':
        return <MetricCard className={cardBorder} platform="Meta + Google" title="Average CTR" value={t ? pctFmt(t.avg_ctr.value) : '—'} change={deltaMeta(t?.avg_ctr).change} trend={deltaMeta(t?.avg_ctr).trend} description="Total clicks ÷ total impressions × 100" onHover={onHover} />;
      case 'repeat_rate':
        return <MetricCard className={cardBorder} platform="Shopify" title="Repeat Rate (Orders)" value={t ? pctFmt(t.repeat_rate.value) : '—'} change={deltaMeta(t?.repeat_rate).change} trend={deltaMeta(t?.repeat_rate).trend} description="Shopify's own metric: returning customers ÷ all customers × 100" onHover={onHover} />;
      case 'cart_recovery_rate':
        return <MetricCard className={cardBorder} platform="Shopify Analytics" title="Cart Recovery Rate" value={t ? pctFmt(t.cart_recovery_rate.value) : '—'} change={deltaMeta(t?.cart_recovery_rate).change} trend={deltaMeta(t?.cart_recovery_rate).trend} description="Recovered checkouts ÷ all abandoned checkouts × 100" onHover={onHover} />;
      case 'cart_recovery_revenue':
        return <MetricCard className={cardBorder} platform="Shopify" title="Cart Recovery Revenue" value={t ? inr(t.cart_recovery_revenue.value) : '—'} change={deltaMeta(t?.cart_recovery_revenue).change} trend={deltaMeta(t?.cart_recovery_revenue).trend} description="Revenue from orders tagged 'abandoned recovered'" onHover={onHover} />;
      case 'assisted_revenue':
        return <MetricCard className={cardBorder} platform="Shopify" title="Total Assisted Revenue" value={t ? inr(t.assisted_revenue.value) : '—'} change={deltaMeta(t?.assisted_revenue).change} trend={deltaMeta(t?.assisted_revenue).trend} description="Revenue from orders tagged 'assisted revenue'" onHover={onHover} />;
      case 'designs_shipped':
        return <MetricCard className={cardBorder} platform="Design Tracker Sheet" title="Designs Shipped" value={t ? num(t.designs_shipped.value) : '—'} change={snapshotMeta(t?.designs_shipped).change} trend={snapshotMeta(t?.designs_shipped).trend} description="Products currently in Sampling (Product Plan sheet)" onHover={onHover} />;
      case 'sampling_finished':
        return <MetricCard className={cardBorder} platform="Design Tracker Sheet" title="Sampling Finished" value={t ? num(t.sampling_finished.value) : '—'} change={snapshotMeta(t?.sampling_finished).change} trend={snapshotMeta(t?.sampling_finished).trend} description="Products currently In Production (Product Plan sheet)" onHover={onHover} />;
      case 'stock_in_rate':
        return <MetricCard className={cardBorder} platform="Shopify" title="Stock-in Rate" value={t ? pctFmt(t.stock_in_rate.value) : '—'} change={snapshotMeta(t?.stock_in_rate).change} trend={snapshotMeta(t?.stock_in_rate).trend} description="SKUs currently in stock ÷ total SKUs tracked × 100" onHover={onHover} />;
      case 'dtc_orders_shipped':
        return <MetricCard className={cardBorder} platform="Unicommerce" title="DTC Orders Shipped" value={t ? num(t.dtc_orders_shipped.value) : '—'} change={deltaMeta(t?.dtc_orders_shipped).change} trend={deltaMeta(t?.dtc_orders_shipped).trend} description="Items in CLOSED manifests (dispatched shipments) in the selected period" onHover={onHover} />;
      default:
        return null;
    }
  };

  if (loading && !data) {
    return (
      <div data-testid="scorecard-loading" className="flex items-center justify-center h-64 text-xs text-slate-500 font-mono uppercase tracking-widest">
        Loading live data…
      </div>
    );
  }

  return (
    <div className="space-y-7 animate-fade-in -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="scorecard-page">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600 bento-glow-dot"></span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] font-semibold">
              Live Operations
            </span>
          </div>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">
            Scorecard
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Pick Weekly, Monthly or Quarterly for an instant preset, or use the calendar for any custom range. Drag any tile to rearrange it.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="scorecard-reset-layout-button"
            onClick={resetOrder}
            title="Reset tile layout"
            className="p-2.5 rounded-xl border bg-white text-slate-500 border-slate-200 hover:border-slate-400 hover:text-slate-800 transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <div data-testid="scorecard-preset-toggle" className="flex items-center gap-1 p-1 rounded-xl bg-white border border-slate-200">
            {(['weekly', 'monthly', 'quarterly'] as Preset[]).map((p) => (
              <button
                key={p}
                data-testid={`scorecard-preset-${p}`}
                onClick={() => handlePreset(p)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${
                  preset === p ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          <DateRangePicker value={range} onChange={handleCalendarChange} minDate={ninetyDaysAgo} maxDate={today} testIdPrefix="scorecard" />
        </div>
      </div>

      <div className="space-y-8" data-testid="scorecard-category-groups">
        {CATEGORIES.map((cat) => (
          <div key={cat.key} data-testid={`scorecard-category-${cat.key}`}>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] font-bold whitespace-nowrap">
                {cat.label}
              </span>
              <div className="flex-1 h-px bg-[#e2d3ba]" />
            </div>
            <SortableTileGrid
              order={order[cat.key] || cat.tiles}
              onReorder={(newOrder) => reorderCategory(cat.key, newOrder)}
              renderTile={renderTile}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              testId={`scorecard-tile-grid-${cat.key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
